import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { DatePicker } from 'antd-mobile';
import './index.scss';
import request from '@/utils/request';
import { createNonceStr, md5, getLocationSearch } from "@/utils/utils";
import { useTranslation } from '@/i18n';

// 腾讯地图API Key
const MAP_API_KEY = 'OXTBZ-JFXRL-PLQPT-MGZTX-ITW7Z-IQFJQ';

// 自定义 Toast 组件
const Toast = {
  show: ({ content, duration = 2000 }) => {
    const toast = document.createElement('div');
    toast.className = 'custom-toast';
    toast.innerText = content;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.classList.add('show');
    }, 10);
    
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        document.body.removeChild(toast);
      }, 300);
    }, duration);
  }
};

// JSONP请求辅助函数
const jsonpRequest = (url) => {
  return new Promise((resolve, reject) => {
    const callbackName = 'jsonp_callback_' + Math.round(100000 * Math.random());
    const timeout = setTimeout(() => {
      delete window[callbackName];
      reject(new Error('请求超时'));
    }, 10000);

    window[callbackName] = (data) => {
      clearTimeout(timeout);
      delete window[callbackName];
      document.body.removeChild(script);
      resolve(data);
    };
    
    const script = document.createElement('script');
    url += (url.indexOf('?') === -1 ? '?' : '&') + 'callback=' + callbackName;
    script.src = url;
    script.onerror = () => {
      clearTimeout(timeout);
      delete window[callbackName];
      reject(new Error('脚本加载失败'));
    };
    document.body.appendChild(script);
  });
};

// 逆地理编码 - 根据经纬度获取地址
const reverseGeocode = async (lat, lng) => {
  try {
    const url = `https://apis.map.qq.com/ws/geocoder/v1/?location=${lat},${lng}&key=${MAP_API_KEY}&output=jsonp`;
    const result = await jsonpRequest(url);
    
    if (result && result.status === 0 && result.result) {
      return {
        address: result.result.address,
        formattedAddress: result.result.formatted_addresses?.recommend || result.result.address,
        lat: lat,
        lng: lng
      };
    }
    return null;
  } catch (error) {
    console.error('逆地理编码失败:', error);
    return null;
  }
};

// 获取当前GPS位置
const getCurrentPosition = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('浏览器不支持定位'));
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
      },
      (error) => {
        let message = '定位失败';
        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = '用户拒绝了定位请求';
            break;
          case error.POSITION_UNAVAILABLE:
            message = '位置信息不可用';
            break;
          case error.TIMEOUT:
            message = '定位请求超时';
            break;
          default:
            message = '未知定位错误';
        }
        reject(new Error(message));
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  });
};

// 自定义时间格式化函数
const formatDateTime = (date) => {
  if (!date) return '';
  
  try {
    const pad = (num) => (num < 10 ? `0${num}` : num);
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}/${month}/${day} ${hours}:${minutes}:${seconds}`;
  } catch (e) {
    console.error('日期格式化错误:', e);
    return '';
  }
};

// 安全的日期解析函数
const parseDateTime = (dateString) => {
  if (!dateString) return new Date();
  if (dateString instanceof Date) return dateString;
  
  try {
    if (typeof dateString === 'string') {
      dateString = dateString.replace(' ', 'T');
      if (dateString.indexOf('Z') === -1 && dateString.indexOf('+') === -1) {
        dateString = dateString + 'Z';
      }
    }
    
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? new Date() : date;
  } catch (e) {
    console.error('日期解析错误:', e);
    return new Date();
  }
};

const BeckonOrder = () => {
  const t = useTranslation();
  const history = useHistory();
  
  // 订单状态：'idle' | 'boarded' | 'completed'
  const [orderStatus, setOrderStatus] = useState('idle');
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [driverInfo, setDriverInfo] = useState(null);
  
  // 上车时间选择器
  const [onTimePickerVisible, setOnTimePickerVisible] = useState(false);
  
  // 表单数据
  const [formData, setFormData] = useState({
    onTime: formatDateTime(new Date()),
    startAddress: '',
    startLat: 0,
    startLng: 0,
    endTime: '',
    endAddress: '',
    endLat: 0,
    endLng: 0,
  });

  // 构建请求参数
  const getBaseParams = () => {
    let token = getLocationSearch().token || localStorage.getItem("userToken") || "";
    let appKey = "app_h5";
    let secret = "11dc72cf-88b0-46cd-91d7-b1a77fd69ee1";
    let timestamp = new Date().getTime();
    let random = createNonceStr();
    let tempStr = `appKey${appKey}secret${secret}timestamp${timestamp}random${random}token${token}`;
    let sign = md5(tempStr, 32).toString(16).toLocaleUpperCase();
    
    return {
      appKey: appKey,
      timestamp: timestamp,
      random: random,
      token: token,
      sign: sign,
    };
  };

  // 获取司机信息
  const fetchDriverInfo = async () => {
    try {
      const params = getBaseParams();
      const driverDetail = localStorage.getItem('driverDetail');
      if (driverDetail) {
        setDriverInfo(JSON.parse(driverDetail));
      } else {
        const response = await request.get('/api/driverUser/checkToken', { token: params.token }, params);
        if (response.data && response.data.errorCode === 0) {
          const driverData = response.data.data;
          setDriverInfo(driverData);
          localStorage.setItem('driverDetail', JSON.stringify(driverData));
        }
      }
    } catch (error) {
      console.error('获取司机信息失败:', error);
    }
  };

  useEffect(() => {
    document.title = t('beckonOrder.pageTitle');
    fetchDriverInfo();
    // 页面加载时自动获取上车定位
    autoLocate('start');
  }, []);

  const handleChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  // 自动获取当前位置
  const autoLocate = async (type) => {
    setLocating(true);
    try {
      // 获取GPS坐标
      const coords = await getCurrentPosition();
      Toast.show({ content: t('beckonOrder.locating'), duration: 1000 });
      
      // 逆地理编码获取地址
      const location = await reverseGeocode(coords.lat, coords.lng);
      
      if (location) {
        if (type === 'start') {
          setFormData(prev => ({
            ...prev,
            startAddress: location.formattedAddress,
            startLat: location.lat,
            startLng: location.lng,
          }));
        } else {
          setFormData(prev => ({
            ...prev,
            endAddress: location.formattedAddress,
            endLat: location.lat,
            endLng: location.lng,
          }));
        }
        Toast.show({ content: t('beckonOrder.locateSuccess'), duration: 1500 });
      } else {
        Toast.show({ content: t('beckonOrder.error.locateFailed'), duration: 2000 });
      }
    } catch (error) {
      console.error('定位失败:', error);
      Toast.show({ content: error.message || t('beckonOrder.error.locateFailed'), duration: 2000 });
    } finally {
      setLocating(false);
    }
  };

  // 乘客上车 - 创建扬招单
  const handlePassengerBoarded = async () => {
    if (!formData.startAddress) {
      Toast.show({ content: t('beckonOrder.error.startAddressRequired'), duration: 2000 });
      return;
    }
    if (!formData.onTime) {
      Toast.show({ content: t('beckonOrder.error.onTimeRequired'), duration: 2000 });
      return;
    }

    setLoading(true);
    try {
      const params = getBaseParams();
      const submitData = {
        driverId: driverInfo?.id || 0,
        onTime: formData.onTime,
        startAddress: formData.startAddress,
        startLat: formData.startLat,
        startLng: formData.startLng,
      };
      
      const response = await request.post('/api/phone/order/offlineBeckonOrderCreate', submitData, params);
      
      if (response.data && (response.data.errorCode === 0 || response.data.code === 200)) {
        const newOrderId = response.data.data;
        setOrderId(newOrderId);
        setOrderStatus('boarded');
        Toast.show({ content: t('beckonOrder.success.boarded'), duration: 2000 });
        // 上车成功后自动获取下车定位
        setTimeout(() => autoLocate('end'), 500);
      } else {
        Toast.show({ content: response.data?.errorMsg || t('beckonOrder.error.createFailed'), duration: 2000 });
      }
    } catch (error) {
      console.error('创建扬招单失败:', error);
      Toast.show({ content: t('beckonOrder.error.createFailed'), duration: 2000 });
    } finally {
      setLoading(false);
    }
  };

  // 乘客下车 - 完成订单
  const handlePassengerDropped = async () => {
    if (!formData.endAddress) {
      Toast.show({ content: t('beckonOrder.error.endAddressRequired'), duration: 2000 });
      return;
    }

    // 自动设置下车时间为当前时间
    const currentEndTime = formatDateTime(new Date());

    setLoading(true);
    try {
      const params = getBaseParams();
      const submitData = {
        driverId: driverInfo?.id || 0,
        endAddress: formData.endAddress,
        endLat: formData.endLat,
        endLng: formData.endLng,
        endTime: currentEndTime,
        orderId: orderId,
      };
      
      const response = await request.post('/api/phone/order/finishOfflineBeckonOrder', submitData, params);
      
      if (response.data && (response.data.errorCode === 0 || response.data.code === 200)) {
        // 更新表单中的下车时间用于显示
        setFormData(prev => ({ ...prev, endTime: currentEndTime }));
        setOrderStatus('completed');
        Toast.show({ content: t('beckonOrder.success.completed'), duration: 2000 });
      } else {
        Toast.show({ content: response.data?.errorMsg || t('beckonOrder.error.completeFailed'), duration: 2000 });
      }
    } catch (error) {
      console.error('完成订单失败:', error);
      Toast.show({ content: t('beckonOrder.error.completeFailed'), duration: 2000 });
    } finally {
      setLoading(false);
    }
  };

  // 重置订单
  const handleReset = () => {
    setOrderStatus('idle');
    setOrderId(null);
    setFormData({
      onTime: formatDateTime(new Date()),
      startAddress: '',
      startLat: 0,
      startLng: 0,
      endTime: '',
      endAddress: '',
      endLat: 0,
      endLng: 0,
    });
  };

  return (
    <div className="beckon-order-container">
      {/* 订单信息卡片 */}
      <div className="order-info-card">
        {/* 状态标签 */}
        <div className={`card-header ${orderStatus}`}>
          <div className="status-indicator">
            <span className="status-dot"></span>
            <span className="status-text">
              {orderStatus === 'idle' && t('beckonOrder.status.idle')}
              {orderStatus === 'boarded' && t('beckonOrder.status.boarded')}
              {orderStatus === 'completed' && t('beckonOrder.status.completed')}
            </span>
          </div>
          {driverInfo && (
            <div className="driver-info">
              <span className="driver-name">{driverInfo.driverName}</span>
              <span className="plate-no">{driverInfo.plateNo || ''}</span>
            </div>
          )}
        </div>

        {/* 上车时间 */}
        <div className="info-row">
          <span className="info-label">{t('beckonOrder.label.onTime')}</span>
          {orderStatus === 'idle' ? (
            <div 
              className="info-value clickable"
              onClick={() => setOnTimePickerVisible(true)}
            >
              {formData.onTime || t('beckonOrder.placeholder.selectTime')}
            </div>
          ) : (
            <span className="info-value">{formData.onTime}</span>
          )}
        </div>
        
        <DatePicker
          visible={onTimePickerVisible}
          value={formData.onTime ? parseDateTime(formData.onTime) : new Date()}
          precision="second"
          onClose={() => setOnTimePickerVisible(false)}
          onConfirm={(val) => {
            handleChange('onTime', formatDateTime(val));
            setOnTimePickerVisible(false);
          }}
        />

        {/* 上车定位 */}
        <div className="info-row">
          <span className="info-label">{t('beckonOrder.label.startAddress')}</span>
          {formData.startAddress ? (
            <span className="info-value address-value">
              {formData.startAddress}
              <span className="gps-tag">(GPS精准定位)</span>
            </span>
          ) : (
            <span className="info-value locating-text">
              {locating ? t('beckonOrder.locating') : t('beckonOrder.error.locateFailed')}
            </span>
          )}
        </div>

        {/* 下车时间 */}
        <div className="info-row">
          <span className="info-label">{t('beckonOrder.label.offTime')}</span>
          {orderStatus === 'completed' ? (
            <span className="info-value">{formData.endTime}</span>
          ) : (
            <span className="info-value placeholder">{t('beckonOrder.placeholder.notDropped')}</span>
          )}
        </div>

        {/* 下车定位 */}
        <div className="info-row">
          <span className="info-label">{t('beckonOrder.label.endAddress')}</span>
          {orderStatus === 'boarded' || orderStatus === 'completed' ? (
            formData.endAddress ? (
              <span className="info-value address-value">
                {formData.endAddress}
                <span className="gps-tag">(GPS精准定位)</span>
              </span>
            ) : (
              <span className="info-value locating-text">
                {locating ? t('beckonOrder.locating') : t('beckonOrder.placeholder.notLocated')}
              </span>
            )
          ) : (
            <span className="info-value placeholder">{t('beckonOrder.placeholder.notLocated')}</span>
          )}
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="action-buttons">
        {orderStatus === 'idle' && (
          <button 
            className="action-btn primary"
            onClick={handlePassengerBoarded}
            disabled={loading}
          >
            {loading ? t('beckonOrder.loading') : t('beckonOrder.action.boarded')}
          </button>
        )}
        
        {orderStatus === 'boarded' && (
          <button 
            className="action-btn success"
            onClick={handlePassengerDropped}
            disabled={loading}
          >
            {loading ? t('beckonOrder.loading') : t('beckonOrder.action.dropped')}
          </button>
        )}
        
        {orderStatus === 'completed' && (
          <button 
            className="action-btn default"
            onClick={handleReset}
          >
            {t('beckonOrder.action.newOrder')}
          </button>
        )}
      </div>
    </div>
  );
};

export default BeckonOrder;
